import React, { lazy, Suspense } from "react";
import { motion } from "framer-motion";
import {
    Compass,
    Target,
    Rocket,
    TrendingUp,
    Users,
    ShieldCheck,
    Sparkles,
    Globe2,
    MapPin,
    Award,
} from "lucide-react";
import { SectionHeading, SectionEyebrow } from "../components/shared/SectionHeading";
import CTABanner from "../components/shared/CTABanner";
import {
    LEADERSHIP,
    TIMELINE,
    LOCATIONS,
    VALUES,
    CERTIFICATIONS,
} from "../lib/data";

const FloatingKnot = lazy(() => import("../components/three/FloatingKnot"));

const ICON_MAP = {
    Compass,
    Users,
    ShieldCheck,
    Sparkles,
    Globe2,
    TrendingUp,
};

export default function About() {
    return (
        <main data-testid="page-about" className="relative pt-32 md:pt-40">
            {/* Hero */}
            <section className="relative pb-14 md:pb-20">
                <div className="absolute inset-0 -z-10 grid-lines opacity-40" />
                <div className="absolute inset-0 -z-10 bg-radial-fade" />
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <SectionEyebrow>Company Overview</SectionEyebrow>
                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7 }}
                            className="mt-5 font-display text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.02]"
                        >
                            We turn bold ideas into
                            <br />
                            <span className="text-gradient-brand">lasting enterprise value.</span>
                        </motion.h1>
                        <p className="mt-7 text-zinc-400 text-lg leading-relaxed max-w-2xl">
                            Netfotech Solutions was founded by a team with over
                            <span className="text-white font-medium"> 25+ years of combined leadership </span>
                            across AI, cloud, cybersecurity and enterprise
                            consulting. We help ambitious organizations embrace
                            the next technology wave safely and sustainably.
                        </p>
                    </div>
                    <div className="lg:col-span-5 h-[360px] md:h-[440px]">
                        <Suspense fallback={<div className="h-full w-full rounded-3xl glass" />}>
                            <FloatingKnot />
                        </Suspense>
                    </div>
                </div>
            </section>

            {/* Aspire / Design / Execute */}
            <section
                data-testid="about-philosophy-section"
                className="relative py-16 md:py-20 border-t border-white/5"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Our Philosophy"
                        title={<>Aspire. Design. Execute.</>}
                        subtitle="A deliberate three step discipline that shapes every engagement we take on from first whiteboard to decade-long partnership."
                    />

                    <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {[
                            {
                                icon: Compass,
                                title: "Aspire",
                                desc: "We listen first. We understand the outcome, the constraints and the ambition behind your transformation.",
                                accent: "from-brand-blue to-brand-blue-2",
                            },
                            {
                                icon: Target,
                                title: "Design",
                                desc: "Architects, designers and domain leads co-create a solution blueprint aligned to business value.",
                                accent: "from-brand-blue to-brand-green",
                            },
                            {
                                icon: Rocket,
                                title: "Execute",
                                desc: "Globally distributed teams deliver production software with governance, quality and SLA discipline.",
                                accent: "from-brand-green-2 to-brand-green",
                            },
                        ].map((p, i) => (
                            <motion.div
                                key={p.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6, delay: i * 0.1 }}
                                className="relative glass rounded-3xl border border-white/5 p-8 brand-border-hover tracing-beam"
                                data-testid={`about-philosophy-${p.title.toLowerCase()}`}
                            >
                                <div className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${p.accent}`}>
                                    <p.icon className="h-6 w-6 text-white" />
                                </div>
                                <div className="mt-6 font-display text-3xl font-semibold tracking-tight">
                                    0{i + 1}. {p.title}
                                </div>
                                <p className="mt-4 text-[15px] text-zinc-400 leading-relaxed">
                                    {p.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Mission & Vision */}
            <section
                data-testid="about-mission-section"
                className="relative py-16 md:py-20 border-t border-white/5"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Our Mission & Vision"
                        title={<>Scalable AI innovation. <span className="text-gradient-brand">Secure digital ecosystems.</span></>}
                        subtitle="The twin outcomes that guide every engagement we take on measured on what we ship, not what we promise."
                    />

                    <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6 }}
                            className="glass rounded-3xl border border-white/5 p-10 relative overflow-hidden"
                        >
                            <div className="absolute -top-32 -right-32 h-72 w-72 rounded-full bg-brand-blue/20 blur-3xl" />
                            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                                Our Mission
                            </h3>
                            <p className="mt-5 text-zinc-300 text-[16px] leading-relaxed">
                                Empower organizations with scalable  AI driven innovation.
                            </p>
                            <p className="mt-4 text-zinc-400 leading-relaxed">
                                We build the platforms, products and practices that let
                                enterprises move from bold ambition to durable outcomes
                                without trading off security, compliance or customer trust.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="glass rounded-3xl border border-white/5 p-10 relative overflow-hidden"
                        >
                            <div className="absolute -bottom-32 -left-32 h-72 w-72 rounded-full bg-brand-green/20 blur-3xl" />
                            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                                Our Vision
                            </h3>
                            <p className="mt-5 text-zinc-300 text-[16px] leading-relaxed">
                                Secure digital ecosystems for a better connected world.
                            </p>
                            <p className="mt-4 text-zinc-400 leading-relaxed">
                                To be the trusted technology partner that enables
                                governments, enterprises and educators to build an
                                AI powered, resilient, secure digital fabric for the
                                next generation.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Market Dynamics */}
            <section
                data-testid="about-market-section"
                className="relative py-16 md:py-20 border-t border-white/5"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Market Dynamics"
                        title="Forces driving technology adoption today."
                        subtitle="Understanding the macro and micro forces reshaping enterprise technology choices and the opportunity hidden inside each."
                    />

                    <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {[
                            {
                                title: "The Generative AI wave",
                                desc: "Copilots, agents and domain tuned models are rewriting the enterprise software stack from SDR to CFO.",
                            },
                            {
                                title: "Regulatory tailwinds",
                                desc: "DPDP, GDPR, HIPAA and sector-specific frameworks demand security first, auditable architectures.",
                            },
                            {
                                title: "Cloud cost discipline",
                                desc: "Boards now expect FinOps grade governance scale is required, but unchecked spend isn't acceptable.",
                            },
                            {
                                title: "Data gravity & sovereignty",
                                desc: "National regulations and data locality drive new architectures for multi region AI workloads.",
                            },
                            {
                                title: "Identity-first security",
                                desc: "Hybrid work has dissolved the perimeter Zero trust is now a board level KPI, not a nice-to-have.",
                            },
                            {
                                title: "Talent density",
                                desc: "Global competition for AI and SRE talent makes engineering partners a strategic, not tactical, choice.",
                            },
                        ].map((m, i) => (
                            <motion.div
                                key={m.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                                className="glass rounded-3xl border border-white/5 p-7 brand-border-hover"
                                data-testid={`about-market-card-${i}`}
                            >
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 border border-white/10">
                                    <TrendingUp className="h-5 w-5 text-brand-green" />
                                </div>
                                <div className="mt-6 font-display text-xl font-semibold tracking-tight">
                                    {m.title}
                                </div>
                                <p className="mt-3 text-[14.5px] text-zinc-400 leading-relaxed">
                                    {m.desc}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* LEADERSHIP TEAM */}
            <section
                data-testid="about-leadership-section"
                className="relative py-16 md:py-20 border-t border-white/5"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Leadership Team"
                        title="25+ years, one leadership bench."
                        subtitle="A senior team that has shipped enterprise technology at global scale and still gets their hands on the keyboard."
                    />
                    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {LEADERSHIP.map((l, i) => (
                            <motion.article
                                key={l.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.55, delay: i * 0.06 }}
                                className="group relative overflow-hidden rounded-3xl border border-white/10 glass brand-border-hover p-7 md:p-8 flex flex-col"
                                data-testid={`leader-${l.id}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="font-mono text-[11px] text-brand-green tracking-[0.22em]">
                                        0{i + 1}
                                    </div>
                                    <span className="h-1.5 w-1.5 rounded-full bg-brand-green animate-pulse" />
                                </div>
                                <div className="mt-7 font-display text-[20px] md:text-[22px] font-semibold tracking-tight leading-tight">
                                    {l.name}
                                </div>
                                <div className="mt-1.5 text-[11px] uppercase tracking-[0.22em] text-brand-green">
                                    {l.role}
                                </div>
                                <p className="mt-4 text-[13.5px] text-zinc-400 leading-relaxed">
                                    {l.bio}
                                </p>
                            </motion.article>
                        ))}
                    </div>
                </div>
            </section>

            {/* OUR JOURNEY / TIMELINE */}
            <section
                data-testid="about-timeline-section"
                className="relative py-16 md:py-20 border-t border-white/5 overflow-hidden"
            >
                <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
                <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Our Journey"
                        title={<>A decade of expansion, innovation, <span className="text-gradient-brand">and global presence.</span></>}
                        subtitle="From a 2017 founding team to a global  AI driven enterprise the milestones that shaped Netfotech."
                    />

                    <ol className="mt-16 relative">
                        <div className="absolute left-[28px] md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-brand-blue/40 via-white/10 to-brand-green/40 md:-translate-x-1/2" />
                        {TIMELINE.map((m, i) => (
                            <motion.li
                                key={m.year}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.55, delay: i * 0.06 }}
                                className={`relative grid md:grid-cols-2 gap-4 md:gap-12 pb-12 md:pb-16 ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}
                                data-testid={`timeline-${m.year}`}
                            >
                                <div className={`md:text-right ${i % 2 === 1 ? "md:text-left" : ""} pl-16 md:pl-0 md:pr-12`}>
                                    <div className="font-display text-4xl md:text-5xl font-bold text-gradient-brand tracking-tighter">
                                        {m.year}
                                    </div>
                                </div>
                                <div className="pl-16 md:pl-12 relative">
                                    <span className="absolute left-[20px] md:left-[-8px] top-2 h-4 w-4 rounded-full bg-brand-linear ring-4 ring-ink-950" />
                                    <h3 className="font-display text-xl md:text-2xl font-semibold tracking-tight">
                                        {m.title}
                                    </h3>
                                    <p className="mt-3 text-[14.5px] text-zinc-400 leading-relaxed max-w-lg">
                                        {m.desc}
                                    </p>
                                </div>
                            </motion.li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* GLOBAL PRESENCE */}
            <section
                data-testid="about-presence-section"
                className="relative py-16 md:py-20 border-t border-white/5"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4 lg:sticky lg:top-28 self-start">
                        <SectionHeading
                            eyebrow="Global Presence"
                            title={<>Ten offices. <span className="text-gradient-brand">One accountable team.</span></>}
                            subtitle="A multi disciplinary global engineering footprint from our Pune headquarters to delivery centres across India, the US, UK and South Africa. Wherever your business runs, a senior practice lead is in your timezone."
                        />
                    </div>

                    <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                        {LOCATIONS.map((loc, i) => (
                            <motion.div
                                key={`${loc.city}-${loc.country}`}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.45, delay: i * 0.04 }}
                                className="group glass rounded-2xl border border-white/5 p-6 brand-border-hover flex items-start gap-4"
                                data-testid={`location-${loc.city.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/5 border border-white/10 shrink-0">
                                    <MapPin className="h-5 w-5 text-brand-blue" />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-baseline justify-between gap-3">
                                        <div className="font-display text-lg font-semibold tracking-tight">
                                            {loc.city}
                                        </div>
                                        <span className="text-[10.5px] uppercase tracking-[0.2em] text-brand-green whitespace-nowrap">
                                            {loc.type}
                                        </span>
                                    </div>
                                    <div className="mt-0.5 text-[13px] text-zinc-400">
                                        {loc.country}
                                    </div>
                                    {loc.note && (
                                        <div className="mt-2 font-mono text-[11.5px] text-zinc-500">
                                            {loc.note}
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CULTURE & VALUES */}
            <section
                data-testid="about-values-section"
                className="relative py-16 md:py-20 border-t border-white/5"
            >
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Culture & Values"
                        title="How we work with each other and with you."
                        subtitle="Six commitments we hold ourselves to. Not slogans, but the filters we hire, review and ship against."
                    />
                    <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {VALUES.map((v, i) => {
                            const Icon = ICON_MAP[v.icon] || Sparkles;
                            return (
                                <motion.div
                                    key={v.title}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.5, delay: i * 0.05 }}
                                    className="glass rounded-3xl border border-white/5 p-7 brand-border-hover tracing-beam"
                                    data-testid={`value-card-${i}`}
                                >
                                    <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/5 border border-white/10">
                                        <Icon className="h-5 w-5 text-brand-green" />
                                    </div>
                                    <div className="mt-6 font-display text-xl font-semibold tracking-tight">
                                        {v.title}
                                    </div>
                                    <p className="mt-3 text-[14.5px] text-zinc-400 leading-relaxed">
                                        {v.desc}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CERTIFICATIONS & COMPLIANCE */}
            <section
                data-testid="about-certifications-section"
                className="relative py-16 md:py-20 border-t border-white/5 overflow-hidden"
            >
                <div className="absolute inset-0 bg-radial-fade opacity-40 pointer-events-none" />
                <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Certifications & Compliance"
                        title="Built for regulated industries."
                        subtitle="We operate under internationally recognized security, privacy and quality frameworks and we audit ourselves rigorously against them."
                    />
                    <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {CERTIFICATIONS.map((c, i) => (
                            <motion.div
                                key={c.code}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.45, delay: i * 0.04 }}
                                whileHover={{ y: -3 }}
                                className="glass rounded-2xl border border-white/10 px-5 py-6 text-center brand-border-hover"
                                data-testid={`cert-${c.code.toLowerCase().replace(/[\s-]+/g, '-')}`}
                            >
                                <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/5 border border-white/10 mx-auto">
                                    <Award className="h-4.5 w-4.5 text-brand-blue" />
                                </div>
                                <div className="mt-4 font-display text-[15.5px] font-semibold text-white tracking-tight">
                                    {c.code}
                                </div>
                                <div className="mt-1 text-[11.5px] text-zinc-500 leading-snug">
                                    {c.name}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CAREERS LINK — removed per user request */}

            <section className="relative py-16">
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <CTABanner
                        eyebrow="Partner with us"
                        title="Your next chapter starts with a conversation."
                        subtitle="Tell us about your ambition and we'll design an engagement model that meets you where you are."
                    />
                </div>
            </section>
        </main>
    );
}
